import React, { useState, useMemo, useCallback } from "react"
import { useMutation } from "react-query"
import { Container, Chip } from "@material-ui/core"
import TableContainerServerSide from "../components/TableContainerServerSide"
import DefaultFilter from "../components/DefaultFilter"
import SelectColumnFilter from "../components/SelectFilter"
import { fetchRevenue } from "../services/deal"
import moment from "moment"

const paymentStatusChip = {
  paid:     "chip-green",
  partial:  "chip-orange",
  awaiting: "chip-red",
}

const serviceChip = {
  "Premier":      "chip-purple",
  "UGC PAYG":     "chip-blue",
  "Subscription": "chip-orange",
  "PAYG":         "chip-gray",
}

const statusChip = {
  "Ongoing":  "chip-green",
  "Finished": "chip-gray",
}

function durationMonths(start, end) {
  if (!start || !end) return "—"
  const months = moment(end).diff(moment(start), "months", true)
  return months > 0 ? Math.ceil(months) : 1
}

function Revenue() {
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [pageCount, setPageCount] = useState(1)

  const { mutate: fetch, isLoading } = useMutation(
    ({ pageIndex, pageSize, search = {} }) =>
      fetchRevenue({ page: pageIndex + 1, limit: pageSize, ...search }),
    {
      onSuccess: (res, { pageSize }) => {
        setRows(res.data || [])
        setTotal(res.total || 0)
        setPageCount(Math.ceil((res.total || 0) / pageSize) || 1)
      },
    }
  )

  const columns = useMemo(() => [
    {
      accessor: "name",
      Header: "Brand",
      Filter: DefaultFilter,
      Cell: ({ row }) => {
        const { id, name, row_type } = row.original
        if (row_type === "deal") {
          return (
            <a href={`/deals/${id}`} style={{ fontWeight: 600, color: "#1a73e8", textDecoration: "none" }}>
              {name}
            </a>
          )
        }
        return <span>{name}</span>
      },
    },
    {
      accessor: "service",
      Header: "Service",
      Filter: SelectColumnFilter,
      FilterOptions: ["Premier", "UGC PAYG", "Subscription", "PAYG"],
      Cell: ({ value }) => (
        <Chip size="small" className={serviceChip[value] || "chip-gray"} label={value || "—"} />
      ),
    },
    {
      accessor: "payment_status",
      Header: "Payment Status",
      disableFilters: true,
      Cell: ({ value }) => (
        <Chip size="small" className={paymentStatusChip[value] || "chip-gray"} label={value} />
      ),
    },
    {
      accessor: "status_name",
      Header: "Project Status",
      disableFilters: true,
      Cell: ({ value }) =>
        value ? <Chip size="small" className={statusChip[value] || "chip-gray"} label={value} /> : <span style={{ color: "#aaa" }}>—</span>,
    },
    {
      accessor: "start_date",
      Header: "Start Date",
      disableFilters: true,
      Cell: ({ value }) => value ? moment(value).format("ll") : "—",
    },
    {
      accessor: "end_date",
      Header: "End Date",
      disableFilters: true,
      Cell: ({ value }) => value ? moment(value).format("ll") : "—",
    },
    {
      id: "duration",
      Header: "Duration (mo)",
      disableFilters: true,
      Cell: ({ row }) => durationMonths(row.original.start_date, row.original.end_date),
    },
    {
      accessor: "type",
      Header: "Type",
      Filter: SelectColumnFilter,
      FilterOptions: ["new", "renewal"],
      Cell: ({ value }) =>
        value ? (
          <Chip
            size="small"
            className={value === "renewal" ? "chip-orange" : "chip-blue"}
            label={value.charAt(0).toUpperCase() + value.slice(1)}
          />
        ) : <span style={{ color: "#aaa" }}>—</span>,
    },
    {
      accessor: "video_count",
      Header: "Units",
      disableFilters: true,
      Cell: ({ value }) => value ?? "—",
    },
    {
      accessor: "country_name",
      Header: "Country",
      Filter: DefaultFilter,
      Cell: ({ value }) => value || "—",
    },
    {
      accessor: "revenue",
      Header: "Revenue",
      disableFilters: true,
      Cell: ({ row }) => {
        const { revenue, currency_symbol } = row.original
        return <span>{currency_symbol}{Number(revenue || 0).toFixed(2)}</span>
      },
    },
    {
      accessor: "cash_collected",
      Header: "Cash Collected",
      disableFilters: true,
      Cell: ({ row }) => {
        const { cash_collected, currency_symbol } = row.original
        return <span>{currency_symbol}{Number(cash_collected || 0).toFixed(2)}</span>
      },
    },
    {
      accessor: "sales",
      Header: "Sales",
      disableFilters: true,
      Cell: ({ row }) => {
        const { sales, currency_symbol } = row.original
        return <span>{currency_symbol}{Number(sales || 0).toFixed(2)}</span>
      },
    },
    {
      accessor: "extra_cogs",
      Header: "COGS",
      disableFilters: true,
      Cell: ({ row }) => {
        const { extra_cogs, currency_symbol } = row.original
        return <span>{currency_symbol}{Number(extra_cogs || 0).toFixed(2)}</span>
      },
    },
    {
      accessor: "gross_income",
      Header: "Gross Income",
      disableFilters: true,
      Cell: ({ row }) => {
        const { gross_income, currency_symbol } = row.original
        const val = Number(gross_income || 0)
        return (
          <span style={{ color: val < 0 ? "#d32f2f" : "inherit" }}>
            {currency_symbol}{val.toFixed(2)}
          </span>
        )
      },
    },
    {
      accessor: "commission_pct",
      Header: "Commission %",
      disableFilters: true,
      Cell: ({ value }) => <span>{value}%</span>,
    },
  ], [])

  const handleRefetchData = useCallback(({ pageIndex, pageSize, search }) => {
    fetch({ pageIndex, pageSize, search })
  }, [])

  return (
    <Container spacing={1} maxWidth="xl">
      <div className="is-flex aic jcsb mt-20 mb-20">
        <h2 className="mt-0 mb-0">Revenue (<i>{total}</i>)</h2>
        <a className="stllr-btn primary btn-md" href="/deals/new">
          Create Deal
        </a>
      </div>

      <TableContainerServerSide
        name="revenue"
        paging={true}
        columns={columns}
        hiddenColumns={[]}
        data={rows}
        hasFilters={true}
        loading={isLoading}
        pageCount={pageCount}
        fetchData={handleRefetchData}
      />
    </Container>
  )
}

export default Revenue
